import type { IconifyIcon } from '@iconify/types'
import { Icon as IconifyIconEl } from '@iconify-icon/solid'
import { type Component, createMemo, Show, splitProps } from 'solid-js'
import { twMerge } from 'tailwind-merge'
import { useAppState } from '~/contexts/ThemeContext'
import type { IconPropsType } from '~/types/icons'

const isIconifyObject = (icon: unknown): icon is IconifyIcon => typeof icon === 'object' && icon !== null && 'body' in icon

const getIconSources = () => {
  try {
    const ctx = useAppState()
    const providerIcons = ctx?.icons?.()
    if (providerIcons) return providerIcons
  } catch {
    // no provider in the host app, ignore
  }
  return {}
}

const resolveComponentFromEntry = (entry: unknown) => {
  if (!entry) return null
  if (typeof entry === 'object' && entry !== null && 'default' in entry) return (entry as Record<string, unknown>).default
  if (typeof entry === 'function' || typeof entry === 'object') return entry
  return null
}

const Icon: Component<IconPropsType> = props => {
  const [local, others] = splitProps(props, ['icon', 'class', 'size', 'lazy'])

  const iconos = createMemo(() => getIconSources())

  const normalized = createMemo(() => {
    const map = new Map<string, unknown>()
    const sources = iconos()
    if (!sources) return map
    try {
      const entries = Object.entries(sources as Record<string, unknown>)
      for (const [k, v] of entries) {
        const comp = resolveComponentFromEntry(v)
        if (!comp) continue
        map.set(k, comp)
        const file = k.split('/').pop() || k
        const noExt = file.replace(/\.svg$/i, '')
        map.set(file, comp)
        map.set(noExt, comp)
        map.set(noExt.replace(/--/g, ':'), comp)
      }
    } catch {
      // ignore
    }
    return map
  })

  const iconComponent = createMemo(() => {
    if (!local.icon) return null
    if (isIconifyObject(local.icon)) return null
    const iconKey = String(local.icon)
    const map = normalized()

    const candidates = [iconKey, iconKey.replace(':', '--'), `${iconKey.replace(':', '--')}.svg`, `${iconKey}.svg`]
    for (const c of candidates) {
      if (map.has(c)) return map.get(c)
    }

    for (const [k, v] of map.entries()) {
      const base = k.split('/').pop()!
      const noExt = base.replace(/\.svg$/i, '')
      if (noExt === iconKey) return v
      if (noExt === iconKey.replace(':', '--')) return v
      if (noExt.replace(/--/g, ':') === iconKey) return v
      if (noExt.includes(iconKey.replace(':', '--'))) return v
      if (iconKey.includes(noExt)) return v
    }

    return null
  })

  return (
    <Show when={local.icon}>
      <Show
        when={!isIconifyObject(local.icon) && iconComponent()}
        fallback={<IconifyIconEl icon={local.icon!} class={local.class} width={local.size || '24'} height={local.size || '24'} noobserver={!local.lazy} />}
      >
        {(() => {
          const Cmp = iconComponent() as Component<Record<string, unknown>>
          return <Cmp class={twMerge('inline-block', local.class)} width={local.size || '24'} height={local.size || '24'} {...(others as Record<string, unknown>)} />
        })()}
      </Show>
    </Show>
  )
}

export default Icon
